<template>
  <!-- Реплика Зои: белая карточка с обводкой line, хвостик к элементу, аватар
       выглядывает из нижнего угла (вариант A из макета владельца 26.10.2026).
       Один на приложение, стоит в App.vue; что показывать — services/guide.js.
       Ничего не затемняет: тап в любое место закрывает, тап по самому
       элементу — просто прячет, «done» запишет экран. -->
  <Teleport to="body">
    <Transition name="guide-bubble">
      <ZoyaBubble
        v-if="active && pos"
        ref="bubbleRef"
        class="guide-bubble"
        :class="{ 'is-hidden': keyboardOpen }"
        :placement="pos.placement"
        :style="{ left: pos.left + 'px', top: pos.top + 'px', width: pos.width + 'px', '--tail-x': pos.tailX + 'px' }"
        role="status"
        aria-live="polite"
      >
        <p class="guide-bubble__text"><template v-for="(part, i) in textParts" :key="i"><span v-if="part.color" class="guide-swatch" :style="{ background: part.color }" role="img" :aria-label="part.label"></span><template v-else>{{ part.text }}</template></template></p>
        <button v-if="active.cta" type="button" class="guide-bubble__ok" @click.stop="guide.cta()">{{ active.cta }}</button>
        <button v-else-if="active.ok" type="button" class="guide-bubble__ok" @click.stop="guide.dismiss(active.key)">Понятно</button>
      </ZoyaBubble>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import ZoyaBubble from './ZoyaBubble.vue'
import { KCAL_DAY_COLORS } from '../utils/kcalDayColor.js'
import guide, { active } from '../services/guide.js'

const swatchLabels = { deficit: 'Зелёный квадрат', surplus: 'Розовый квадрат', maintenance: 'Бежевый квадрат' }
const textParts = computed(() => (active.value?.text || '').split(/(\{(?:deficit|surplus|maintenance)\})/g).filter(Boolean).map(text => {
  const key = text.slice(1, -1)
  return KCAL_DAY_COLORS[key] ? { color: KCAL_DAY_COLORS[key], label: swatchLabels[key] } : { text }
}))
const bubbleRef = ref(null)
const pos = ref(null)
const keyboardOpen = ref(false)

const GAP = 14           // зазор между элементом и пузырём
const MARGIN = 12        // от края экрана
const MAX_WIDTH = 264
const COLUMN_WIDTH = 430   // max-w-[430px] у приложения

let raf = 0
let lastEl = null

function resolveAnchor(anchor) {
  if (typeof anchor === 'function') return anchor()
  if (typeof anchor === 'string') return document.querySelector(anchor)
  return anchor || null
}

// Позиция считается каждый кадр, пока пузырь виден: элемент может ехать
// (прокрутка, анимация шторки), а один пузырь на экране — это дёшево.
function tick() {
  raf = 0
  const a = active.value
  if (!a) return
  const el = resolveAnchor(a.anchor)
  if (!el || !el.isConnected) {
    // Элемент пропал (экран перерисовался) — прячем без записи, экран попросит снова
    guide.hide(a.key)
    return
  }
  lastEl = el
  const r = el.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.visualViewport?.height || window.innerHeight
  keyboardOpen.value = vh < window.innerHeight * 0.78
  // Contextual analytics tips stay with their section instead of sticking to a screen edge.
  if (a.key.startsWith('an_') && !a.key.startsWith('an_pick_') && (r.bottom < vh * .1 || r.top > vh * .85)) {
    pos.value = null
    raf = requestAnimationFrame(tick)
    return
  }
  // На широком экране приложение — колонка 430 px по центру; пузырь из неё не выходит
  const colW = Math.min(vw, COLUMN_WIDTH)
  const colLeft = Math.floor((vw - colW) / 2)
  const width = Math.min(MAX_WIDTH, colW - MARGIN * 2)
  let left = r.left + r.width / 2 - width / 2
  left = Math.max(colLeft + MARGIN, Math.min(left, colLeft + colW - width - MARGIN))
  const tailX = Math.max(22, Math.min(r.left + r.width / 2 - left, width - 22))
  const h = bubbleRef.value?.$el?.offsetHeight || 64
  let placement = a.place
  let top = placement === 'below' ? r.bottom + GAP : r.top - GAP - h
  if (top < MARGIN) { placement = 'below'; top = r.bottom + GAP }
  if (top + h > vh - MARGIN) { placement = 'above'; top = Math.max(MARGIN, r.top - GAP - h) }
  pos.value = { left, top, width, tailX, placement }
  raf = requestAnimationFrame(tick)
}

function onPointerDown(e) {
  const a = active.value
  if (!a) return
  if (bubbleRef.value?.$el?.contains(e.target)) {
    if (!a.ok && !a.cta) guide.dismiss(a.key)
    return
  }
  // Реплика на месте пустого списка (sticky) живёт, пока список пуст: тап
  // мимо неё ничего не значит, закрывает только тап по ней самой
  if (a.sticky) return
  // Тап по самому элементу — это действие: прячем, «done» запишет экран
  if (lastEl && lastEl.contains(e.target)) { guide.hide(a.key); return }
  guide.dismiss(a.key)
}

watch(active, (a) => {
  if (a) {
    pos.value = null
    if (!raf) raf = requestAnimationFrame(tick)
    document.addEventListener('pointerdown', onPointerDown, true)
  } else {
    if (raf) { cancelAnimationFrame(raf); raf = 0 }
    pos.value = null
    lastEl = null
    document.removeEventListener('pointerdown', onPointerDown, true)
  }
}, { immediate: true })

onUnmounted(() => {
  if (raf) cancelAnimationFrame(raf)
  document.removeEventListener('pointerdown', onPointerDown, true)
})
</script>

<style scoped>
.guide-bubble { position: fixed; z-index: 120; pointer-events: auto; }
.guide-bubble.is-hidden { visibility: hidden; }
.guide-bubble__text { margin: 0; }
.guide-swatch { display: inline-block; width: 13px; height: 13px; border-radius: 3px; vertical-align: -1px; }
.guide-bubble__ok { display: inline-block; margin-top: 8px; padding: 0; background: none; border: 0; font: 600 var(--fs-12) Inter, sans-serif; color: var(--warm); }
.guide-bubble-enter-active { transition: opacity .22s ease-out, transform .22s ease-out; }
.guide-bubble-leave-active { transition: opacity .15s ease-in; }
.guide-bubble-enter-from { opacity: 0; transform: translateY(6px); }
.guide-bubble-leave-to { opacity: 0; }
@media (prefers-reduced-motion: reduce) { .guide-bubble-enter-active, .guide-bubble-leave-active { transition: none; } }
</style>
